<template>
  <div>
    <b-card no-body class="mt-1">
      <app-collapse accordion>
        <app-collapse-item title="Earlier Court Details" v-if="earlierCourtDetails.length">
          <div v-for="(item, idx) in earlierCourtDetails.slice(0, earlierCourtSliceLimit)" :key="idx" class="mb-2">
            <b-row v-for="(value, key) in item" :key="key" class="px-2 py-50">
              <b-col cols="12" md="4" class="text-muted">
                {{ formatKey(key) }} :
              </b-col>
              <b-col cols="12" md="8">
                <span class="mr-1">•</span>{{ value || '-' }}
              </b-col>
            </b-row>
            <hr v-if="idx < earlierCourtDetails.slice(0, earlierCourtSliceLimit).length - 1" class="my-2" />
          </div>
          <div class="text-center" v-if="earlierCourtDetails.length > 1">
            <b-button
              v-ripple.400="'rgba(113, 102, 240, 0.15)'"
              variant="light"
              class="btn btn-sm text-primary my-1"
              @click="toggleEarlierCourt"
            >
              {{ isEarlierCourtExpanded ? 'View Less' : 'View More' }}
            </b-button>
          </div>
        </app-collapse-item>

        <app-collapse-item title="Listing Dates" v-if="listingDates.length">
          <div v-for="(item, idx) in listingDates.slice(0, listingDatesSliceLimit)" :key="idx" class="mb-2">
            <b-row v-for="(value, key) in item" :key="key" class="px-2 py-50">
              <b-col cols="12" md="4" class="text-muted">
                {{ formatKey(key) }} :
              </b-col>
              <b-col cols="12" md="8">
                <span class="mr-1">•</span>{{ value || '-' }}
              </b-col>
            </b-row>
            <hr v-if="idx < listingDates.slice(0, listingDatesSliceLimit).length - 1" class="my-2" />
          </div>
          <div class="text-center" v-if="listingDates.length > 1">
            <b-button
              v-ripple.400="'rgba(113, 102, 240, 0.15)'"
              variant="light"
              class="btn btn-sm text-primary my-1"
              @click="toggleListingDates"
            >
              {{ isListingDatesExpanded ? 'View Less' : 'View More' }}
            </b-button>
          </div>
        </app-collapse-item>

        <app-collapse-item title="Court Fees" v-if="courtFees.length">
          <div v-for="(item, idx) in courtFees.slice(0, courtFeesSliceLimit)" :key="idx" class="mb-2">
            <b-row v-for="(value, key) in item" :key="key" class="px-2 py-50">
              <b-col cols="12" md="4" class="text-muted">
                {{ formatKey(key) }} :
              </b-col>
              <b-col cols="12" md="8">
                <span class="mr-1">•</span>{{ value || '-' }}
              </b-col>
            </b-row>
          </div>
          <div class="text-center" v-if="courtFees.length > 1">
            <b-button
              v-ripple.400="'rgba(113, 102, 240, 0.15)'"
              variant="light"
              class="btn btn-sm text-primary my-1"
              @click="toggleCourtFees"
            >
              {{ isCourtFeesExpanded ? 'View Less' : 'View More' }}
            </b-button>
          </div>
        </app-collapse-item>

        <app-collapse-item title="Interlocutory Application Documents" v-if="hasInterlocutoryDocs">
          <div v-if="interlocutoryApplications.length" class="mb-2">
            <h6 class="px-2 pb-1 font-weight-bold">Interlocutory Applications</h6>
            <div v-for="(item, idx) in interlocutoryApplications.slice(0, interlocutoryAppsSliceLimit)" :key="idx" class="mb-2">
              <b-row v-for="(value, key) in item" :key="key" class="px-2 py-50">
                <b-col cols="12" md="4" class="text-muted">
                  {{ formatKey(key) }} :
                </b-col>
                <b-col cols="12" md="8">
                  <span class="mr-1">•</span>{{ value || '-' }}
                </b-col>
              </b-row>
              <hr v-if="idx < interlocutoryApplications.slice(0, interlocutoryAppsSliceLimit).length - 1" class="my-2" />
            </div>
            <div class="text-center" v-if="interlocutoryApplications.length > 1">
              <b-button
                v-ripple.400="'rgba(113, 102, 240, 0.15)'"
                variant="light"
                class="btn btn-sm text-primary my-1"
                @click="toggleInterlocutoryApps"
              >
                {{ isInterlocutoryAppsExpanded ? 'View Less' : 'View More' }}
              </b-button>
            </div>
          </div>

          <div v-if="otherDocuments.length" class="mb-2">
            <h6 class="px-2 pb-1 font-weight-bold">Other Documents</h6>
            <div v-for="(item, idx) in otherDocuments.slice(0, otherDocsSliceLimit)" :key="idx" class="mb-2">
              <b-row v-for="(value, key) in item" :key="key" class="px-2 py-50">
                <b-col cols="12" md="4" class="text-muted">
                  {{ formatKey(key) }} :
                </b-col>
                <b-col cols="12" md="8">
                  <span class="mr-1">•</span>{{ value || '-' }}
                </b-col>
              </b-row>
              <hr v-if="idx < otherDocuments.slice(0, otherDocsSliceLimit).length - 1" class="my-2" />
            </div>
            <div class="text-center" v-if="otherDocuments.length > 1">
              <b-button
                v-ripple.400="'rgba(113, 102, 240, 0.15)'"
                variant="light"
                class="btn btn-sm text-primary my-1"
                @click="toggleOtherDocs"
              >
                {{ isOtherDocsExpanded ? 'View Less' : 'View More' }}
              </b-button>
            </div>
          </div>
          <p v-if="!otherDocuments.length && !interlocutoryApplications.length" class="m-2 text-muted">No documents found.</p>
        </app-collapse-item>

        <app-collapse-item title="Similarities" v-if="similarities.length">
          <div v-for="(group, groupIdx) in similarities" :key="groupIdx" class="mb-2">
            <h6 class="px-2 pb-1 font-weight-bold">{{ group.section || 'Section' }}</h6>
            <div v-if="group.records && group.records.length">
              <div v-for="(item, idx) in group.records.slice(0, getSimilaritiesSliceLimit(groupIdx, group.records.length))" :key="idx" class="mb-2">
                <b-row v-for="(value, key) in item" :key="key" class="px-2 py-50">
                  <b-col cols="12" md="4" class="text-muted">
                    {{ formatKey(key) }} :
                  </b-col>
                  <b-col cols="12" md="8">
                    <span class="mr-1">•</span>{{ value || '-' }}
                  </b-col>
                </b-row>
                <hr v-if="idx < group.records.slice(0, getSimilaritiesSliceLimit(groupIdx, group.records.length)).length - 1" class="my-2" />
              </div>
              <div class="text-center" v-if="group.records.length > 1">
                <b-button
                  v-ripple.400="'rgba(113, 102, 240, 0.15)'"
                  variant="light"
                  class="btn btn-sm text-primary my-1"
                  @click="toggleSimilarities(groupIdx)"
                >
                  {{ similaritiesExpandedStates[groupIdx] ? 'View Less' : 'View More' }}
                </b-button>
              </div>
            </div>
            <p v-else class="m-2 text-muted">No records found.</p>
            <hr v-if="groupIdx < similarities.length - 1" class="my-2" />
          </div>
        </app-collapse-item>

        <app-collapse-item title="Caveat" v-if="caveatDetails.length">
          <div v-for="(item, idx) in caveatDetails.slice(0, caveatSliceLimit)" :key="idx" class="mb-2">
            <b-row v-for="(value, key) in item" :key="key" class="px-2 py-50">
              <b-col cols="12" md="4" class="text-muted">
                {{ formatKey(key) }} :
              </b-col>
              <b-col cols="12" md="8">
                <span class="mr-1">•</span>{{ value || '-' }}
              </b-col>
            </b-row>
            <hr v-if="idx < caveatDetails.slice(0, caveatSliceLimit).length - 1" class="my-2" />
          </div>
          <div class="text-center" v-if="caveatDetails.length > 1">
            <b-button
              v-ripple.400="'rgba(113, 102, 240, 0.15)'"
              variant="light"
              class="btn btn-sm text-primary my-1"
              @click="toggleCaveat"
            >
              {{ isCaveatExpanded ? 'View Less' : 'View More' }}
            </b-button>
          </div>
        </app-collapse-item>
      </app-collapse>
    </b-card>
  </div>
</template>

<script>
import { BCard, BRow, BCol, BButton } from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import AppCollapse from '@core/components/app-collapse/AppCollapse.vue'
import AppCollapseItem from '@core/components/app-collapse/AppCollapseItem.vue'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BButton,
    AppCollapse,
    AppCollapseItem
  },
  directives: {
    Ripple
  },
  props: {
    matter: {
      default: null
    }
  },
  data() {
    return {
      earlierCourtSliceLimit: 1,
      isEarlierCourtExpanded: false,
      listingDatesSliceLimit: 1,
      isListingDatesExpanded: false,
      courtFeesSliceLimit: 1,
      isCourtFeesExpanded: false,
      interlocutoryAppsSliceLimit: 1,
      isInterlocutoryAppsExpanded: false,
      otherDocsSliceLimit: 1,
      isOtherDocsExpanded: false,
      caveatSliceLimit: 1,
      isCaveatExpanded: false,
      similaritiesExpandedStates: {}
    }
  },
  computed: {
    source() {
      return this.matter || {}
    },
    earlierCourtDetails() {
      return Array.isArray(this.source.earlier_court_details)
        ? this.source.earlier_court_details
        : []
    },
    listingDates() {
      return Array.isArray(this.source.listing_dates)
        ? this.source.listing_dates
        : []
    },
    courtFees() {
      if (!this.source.court_fees || Array.isArray(this.source.court_fees)) {
        return []
      }
      return [this.source.court_fees]
    },
    interlocutoryApplications() {
      const docs = this.source.interlocutory_application_documents
      if (!docs || !Array.isArray(docs.interlocutory_applications)) return []
      return docs.interlocutory_applications
    },
    otherDocuments() {
      const docs = this.source.interlocutory_application_documents
      if (!docs || !Array.isArray(docs.other_documents)) return []
      return docs.other_documents
    },
    hasInterlocutoryDocs() {
      return !!this.source.interlocutory_application_documents
    },
    similarities() {
      return Array.isArray(this.source.similarities)
        ? this.source.similarities
        : []
    },
    caveatDetails() {
      return Array.isArray(this.source.caveat) ? this.source.caveat : []
    }
  },
  methods: {
    formatKey(key) {
      return key
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    },
    toggleEarlierCourt() {
      if (this.isEarlierCourtExpanded) {
        this.earlierCourtSliceLimit = 1
        this.isEarlierCourtExpanded = false
      } else {
        this.earlierCourtSliceLimit = this.earlierCourtDetails.length
        this.isEarlierCourtExpanded = true
      }
    },
    toggleListingDates() {
      if (this.isListingDatesExpanded) {
        this.listingDatesSliceLimit = 1
        this.isListingDatesExpanded = false
      } else {
        this.listingDatesSliceLimit = this.listingDates.length
        this.isListingDatesExpanded = true
      }
    },
    toggleCourtFees() {
      if (this.isCourtFeesExpanded) {
        this.courtFeesSliceLimit = 1
        this.isCourtFeesExpanded = false
      } else {
        this.courtFeesSliceLimit = this.courtFees.length
        this.isCourtFeesExpanded = true
      }
    },
    toggleInterlocutoryApps() {
      if (this.isInterlocutoryAppsExpanded) {
        this.interlocutoryAppsSliceLimit = 1
        this.isInterlocutoryAppsExpanded = false
      } else {
        this.interlocutoryAppsSliceLimit = this.interlocutoryApplications.length
        this.isInterlocutoryAppsExpanded = true
      }
    },
    toggleOtherDocs() {
      if (this.isOtherDocsExpanded) {
        this.otherDocsSliceLimit = 1
        this.isOtherDocsExpanded = false
      } else {
        this.otherDocsSliceLimit = this.otherDocuments.length
        this.isOtherDocsExpanded = true
      }
    },
    toggleCaveat() {
      if (this.isCaveatExpanded) {
        this.caveatSliceLimit = 1
        this.isCaveatExpanded = false
      } else {
        this.caveatSliceLimit = this.caveatDetails.length
        this.isCaveatExpanded = true
      }
    },
    toggleSimilarities(groupIdx) {
      this.$set(this.similaritiesExpandedStates, groupIdx, !this.similaritiesExpandedStates[groupIdx])
    },
    getSimilaritiesSliceLimit(groupIdx, recordsLength) {
      return this.similaritiesExpandedStates[groupIdx] ? recordsLength : 1
    }
  }
}
</script>
